if (!customElements.get('mto-purchase-drawer')) {
  customElements.define(
    'mto-purchase-drawer',
    class MtoPurchaseDrawer extends HTMLElement {
      constructor() {
        super();

        this.isOpen = false;
        this.lastFocusedElement = null;
        this.closeTimer = null;
        this.sizePickers = new Map();
        this.boundInlineActions = new WeakSet();
        this.customDisplayValues = new Map();
        this.customFlowActive = false;
        this.customReturnVariantId = null;
        this.syncVariantMediaWhenReady = () => {
          if (this.selectedVariant) this.syncSelectedMedia(this.selectedVariant);
        };
        this.priceSelectionCommitted = new URLSearchParams(location.search).has('variant');
        this.storageKey = `mto-purchase-v2-${this.dataset.productId}`;
        this.unitStorageKey = 'vorug-size-unit';
        this.state = {
          step: 'sizes',
          unit: this.readStoredUnit(),
          selectionMode: null,
          variantId: null,
          payment: null,
          customerType: null,
          custom: {
            lengthMm: null,
            widthMm: null,
            valid: false
          }
        };

        this.handleKeydown = this.handleKeydown.bind(this);
        this.openDrawer = this.openDrawer.bind(this);
        this.closeDrawer = this.closeDrawer.bind(this);
        this.openHomepageChooser = this.openHomepageChooser.bind(this);
        this.syncSticky = this.syncSticky.bind(this);
      }

      connectedCallback() {
        try {
          this.cacheElements();
          this.copy = JSON.parse(this.querySelector('[data-mto-text]').textContent);
          this.bindEvents();
          this.setUnit(this.state.unit, { preserveCustom: false });
          requestAnimationFrame(() => {
            try {
              this.mountProductActions();
              if (this.actionInstances?.length) document.dispatchEvent(new CustomEvent('vorug:pdp-chooser-ready'));
            } catch (error) {
              document.dispatchEvent(new CustomEvent('vorug:pdp-chooser-failed'));
              console.error('Vorug purchase controls could not initialize.', error);
            }
          });
          document.addEventListener('keydown', this.handleKeydown);
          window.addEventListener('hashchange', this.openHomepageChooser);
          document.addEventListener('vorug:media-pin-change', this.syncSticky);
        } catch (error) {
          document.dispatchEvent(new CustomEvent('vorug:pdp-chooser-failed'));
          console.error('Vorug purchase controls could not initialize.', error);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('hashchange', this.openHomepageChooser);
        document.removeEventListener('vorug:media-pin-change', this.syncSticky);
        this.variantMediaGallery?.removeEventListener('vorug:gallery-ready', this.syncVariantMediaWhenReady);
        this.variantImageFade?.cleanup();
        this.variantMediaGallery = null;
        this.cartObserver?.disconnect();
        this.stickyObserver?.disconnect();
        this.stickyStateObserver?.disconnect();
        this.stickyBound = false;
        this.unsubscribeCartUpdate?.();
        this.unsubscribeCartError?.();
        this.sizePickers.forEach((picker) => picker.destroy());
        this.sizePickers.clear();
        this.boundInlineActions = new WeakSet();
        this.actionsMountedRoot = null;
      }

      readStoredUnit() {
        try {
          return localStorage.getItem('vorug-size-unit') === 'm' ? 'm' : 'ft';
        } catch (error) {
          return 'ft';
        }
      }

      cacheElements() {
        this.actionsTemplate = this.querySelector('[data-mto-actions]');
        this.overlay = this.querySelector('[data-mto-overlay]');
        this.drawer = this.querySelector('[data-mto-drawer]');
        this.steps = Array.from(this.querySelectorAll('[data-mto-step]'));
        this.sizeInputs = Array.from(this.querySelectorAll('[data-mto-size]'));
        this.sizeOptionIndex = Number(this.dataset.sizeOptionIndex) || 0;
        this.variantOptionValues = new Map(this.sizeInputs.map((input) => [input.value, input.dataset.variantOptions ? JSON.parse(input.dataset.variantOptions) : [input.dataset.variantTitle]]));
        this.sizeLabels = Array.from(this.querySelectorAll('[data-mto-size-label]'));
        this.paymentInputs = Array.from(this.querySelectorAll('[data-mto-payment]'));
        this.unitButtons = Array.from(this.querySelectorAll('[data-mto-unit]'));
        this.nextButton = this.querySelector('[data-mto-payment-next]');
        this.addButton = this.querySelector('[data-mto-add]');
        this.customNextButton = this.querySelector('[data-mto-custom-next]');
        this.presetSubtotal = this.querySelector('[data-mto-subtotal="preset"]');
        this.paymentSubtotal = this.querySelector('[data-mto-subtotal="payment"]');
        this.fullPriceOutput = this.querySelector('[data-mto-full-price]');
        this.halfPriceOutputs = Array.from(this.querySelectorAll('[data-mto-half-price], [data-mto-half-due]'));
        this.statusOutput = this.querySelector('[data-mto-status]');
        this.requestForm = this.querySelector('.mto-request-form');
        this.personalFields = this.querySelector('[data-mto-personal-fields]');
        this.personalInputs = Array.from(this.querySelectorAll('[data-mto-personal-input]'));
        this.proSummary = this.querySelector('[data-mto-pro-summary]');
        this.proEmailInput = this.querySelector('[data-mto-pro-email]');
        this.requestTitle = this.querySelector('[data-mto-request-title]');
        this.requestIntro = this.querySelector('[data-mto-request-intro]');
        this.requestFields = new Map(
          Array.from(this.querySelectorAll('[data-mto-request-field]')).map((field) => [field.dataset.mtoRequestField, field])
        );
        this.customInputs = new Map(
          Array.from(this.querySelectorAll('[data-mto-custom-input]')).map((input) => [input.dataset.mtoCustomInput, input])
        );
        this.customFieldGroups = Array.from(this.querySelectorAll('[data-mto-custom-fields]'));
        this.customErrors = new Map(
          Array.from(this.querySelectorAll('[data-mto-custom-error]')).map((error) => [error.dataset.mtoCustomError, error])
        );
      }

      bindEvents() {
        this.overlay.addEventListener('click', this.closeDrawer);
        this.querySelector('[data-mto-close]').addEventListener('click', this.closeDrawer);
        this.querySelector('[data-mto-back]').addEventListener('click', () => { this.closeDrawer(); this.focusInlineChooser(); });
        this.querySelector('[data-mto-custom-back]').addEventListener('click', () => { this.closeDrawer(); this.focusInlineChooser(); });
        this.querySelector('[data-mto-customer-back]').addEventListener('click', () => {
          this.showStep(this.state.selectionMode === 'custom' ? 'custom' : 'payment');
        });
        this.querySelector('[data-mto-request-back]').addEventListener('click', () => this.showStep(this.state.selectionMode === 'custom' ? 'custom' : 'customer-type'));
        this.querySelector('[data-mto-success-close]').addEventListener('click', this.closeDrawer);
        this.nextButton.addEventListener('click', () => {
          if (this.state.selectionMode === 'preset' && this.selectedVariant) this.showStep('payment');
        });
        this.customNextButton.addEventListener('click', () => {
          if (this.validateCustom(true)) this.chooseCustomerType('personal');
        });
        this.addButton.addEventListener('click', () => {
          if (this.state.payment === 'full') this.addFullPaymentToCart();
        });

        this.sizeInputs.forEach((input) => input.addEventListener('change', () => this.selectSize(input)));
        this.paymentInputs.forEach((input) => input.addEventListener('change', () => this.selectPayment(input)));
        this.unitButtons.forEach((button) => button.addEventListener('click', () => this.setUnit(button.dataset.mtoUnit)));
        this.customInputs.forEach((input) => {
          input.addEventListener('input', () => this.validateCustom(false));
          input.addEventListener('blur', () => this.validateCustom(false));
        });
        this.querySelectorAll('[data-mto-customer-type]').forEach((button) => {
          button.addEventListener('click', () => this.chooseCustomerType(button.dataset.mtoCustomerType));
        });
        this.querySelectorAll('[data-mto-guide-link]').forEach((link) => {
          link.addEventListener('click', () => this.saveState({ reason: 'guide' }));
        });
        this.requestForm.addEventListener('submit', () => this.prepareRequestPayload(true));
      }

      mountProductActions() {
        const productRoot = document.querySelector('.main-product');
        if (!productRoot) return;
        if (this.actionsMountedRoot === productRoot && this.actionInstances?.every((actions) => actions.isConnected)) return;

        const nativeBuyBlocks = Array.from(productRoot.querySelectorAll('.buy-buttons'));
        if (nativeBuyBlocks.length === 0) return;

        this.nativeForm =
          productRoot.querySelector('.buy-buttons product-form form[data-type="add-to-cart-form"]') ||
          productRoot.querySelector('product-form form[data-type="add-to-cart-form"]');

        // Additional placements start from pristine markup, before the first picker adds controls.
        const pristineActions = this.actionsTemplate.cloneNode(true);
        this.actionInstances = nativeBuyBlocks.map((block, index) => {
          const actions = index === 0 ? this.actionsTemplate : pristineActions.cloneNode(true);
          actions.hidden = false;
          actions.querySelectorAll('[data-mto-tassel-radio]').forEach((radio) => { radio.name = `${radio.name}-${index}`; });
          if (index > 0) {
            actions.id = `vorug-mto-chooser-${index}`;
            const renamedIds = new Map();
            actions.querySelectorAll('[id]').forEach((field) => {
              const original = field.id;
              field.id = `${original}-${index}`;
              renamedIds.set(original, field.id);
            });
            actions.querySelectorAll('[for], [aria-describedby], [aria-labelledby], [aria-controls], [aria-activedescendant]').forEach((element) => {
              ['for', 'aria-describedby', 'aria-labelledby', 'aria-controls', 'aria-activedescendant'].forEach((attribute) => {
                if (!element.hasAttribute(attribute)) return;
                const references = element.getAttribute(attribute).split(/\s+/).map((id) => renamedIds.get(id) || id);
                element.setAttribute(attribute, references.join(' '));
              });
            });
          }
          this.bindInlineActions(actions);
          block.before(actions);
          block.classList.add('mto-native-control--hidden');
          return actions;
        });

        this.actionsMountedRoot = productRoot;
        this.unitButtons = [...this.unitButtons, ...this.actionInstances.slice(1).flatMap((actions) => Array.from(actions.querySelectorAll('[data-mto-unit]')))];
        this.observeNativeCart();
        this.mountStickyActions();
        this.syncInline();
        productRoot.querySelectorAll('[id^="price-"]').forEach((price) => {
          if (productRoot.querySelector('[data-mto-heading-rate]')) price.classList.add('mto-native-control--hidden');
        });

        const deepLinkedVariantId = new URLSearchParams(window.location.search).get('variant');
        const deepLinkedSize = this.sizeInputs.find((input) => input.value === String(deepLinkedVariantId));

        this.track('pdp_product_view', {
          variant_id: deepLinkedVariantId || null,
          traffic_source: new URLSearchParams(window.location.search).get('utm_source') || null
        });

        productRoot.querySelectorAll('variant-radios, variant-selects, .product-form__quantity').forEach((control) => {
          control.classList.add('mto-native-control--hidden');
        });

        document.querySelectorAll('.sticky-cart__form').forEach((stickyForm) => {
          const stickyWrapper = stickyForm.closest('animate-sticky') || stickyForm;
          stickyWrapper.classList.add('mto-native-control--hidden');
        });

        const ownRequestResponse = this.contactResponseBelongsHere();
        if (ownRequestResponse && (this.querySelector('[data-mto-contact-success]') || new URLSearchParams(window.location.search).get('mto_request') === 'sent')) {
          this.openDrawer({ step: 'success' });
          return;
        }

        const requestErrors = this.querySelector('.mto-form-errors');
        if (requestErrors) requestErrors.hidden = !ownRequestResponse;
        if (ownRequestResponse && requestErrors) {
          this.openDrawer({ step: 'request' });
          return;
        }

        if (deepLinkedSize) {
          deepLinkedSize.checked = true;
          this.selectSize(deepLinkedSize, { track: false });
        } else {
          this.priceSelectionCommitted = false;
          const initialSize = this.sizeInputs.find((input) => !input.disabled && Number(input.dataset.priceCents) > 0 && this.variantOptionValues.get(input.value).some((value) => /both sides|2 sides/i.test(value)))
            || this.sizeInputs.find((input) => !input.disabled && Number(input.dataset.priceCents) > 0)
            || this.sizeInputs.find((input) => Number(input.dataset.priceCents) > 0)
            || this.sizeInputs[0];
          if (initialSize) { initialSize.checked = true; this.selectSize(initialSize, { track: false, updateUrl: false }); }
        }
        if (location.hash === '#vorug-mto-chooser') this.openHomepageChooser();
      }

      bindInlineActions(actions) {
        if (this.boundInlineActions.has(actions)) return;
        this.boundInlineActions.add(actions);
        const optionControl = '[data-mto-size-trigger], [data-mto-inline-size], [data-mto-native-option], .mto-tassel-choice, [data-mto-unit], [data-mto-color], [data-vorug-family-kind], .mto-select-option, .mto-select-custom-tile, .mto-select-custom input';
        const compactInfo = (event) => {
          if (event.isTrusted && event.target.closest?.(optionControl)) actions.closest('.main-product')?.classList.add('is-configuring');
        };
        actions.addEventListener('click', compactInfo, true);
        actions.addEventListener('change', compactInfo, true);
        actions.addEventListener('input', compactInfo, true);
        actions.addEventListener('keydown', (event) => {
          if (!event.altKey && !event.ctrlKey && !event.metaKey && (event.key.length === 1 || ['Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key))) compactInfo(event);
        }, true);
        actions.querySelector('[data-mto-inline-size]').addEventListener('change', (event) => {
          const input = this.sizeInputs.find((candidate) => candidate.value === event.target.value);
          if (input) {
            input.checked = true;
            this.selectSize(input);
          } else this.clearInlineSelection();
        });
        actions.querySelector('[data-mto-inline-add]').addEventListener('click', () => {
          this.state.payment = 'full';
          this.addFullPaymentToCart();
        });
        actions.querySelector('[data-mto-inline-payment]').addEventListener('click', () => {
          if (!this.canPurchase()) return;
          this.openDrawer({ step: 'payment' });
        });
        actions.querySelector('[data-mto-color]')?.addEventListener('change', (event) => {
          const target = new URL(event.target.value, location.origin);
          if (target.origin !== location.origin || !target.pathname.startsWith('/products/')) return;
          target.hash = 'vorug-mto-chooser';
          location.assign(target);
        });
        if (actions !== this.actionsTemplate) actions.querySelectorAll('[data-mto-unit]').forEach((button) => button.addEventListener('click', () => this.setUnit(button.dataset.mtoUnit)));
        actions.querySelector('[data-mto-guide-link]')?.addEventListener('click', () => this.saveState({ reason: 'guide' }));
        this.enhanceSizeSelect(actions);
        actions.querySelectorAll('[data-mto-tassel-radio]').forEach((radio) => {
          radio.addEventListener('change', () => {
            if (!radio.checked) return;
            const index = Number(radio.closest('[data-mto-tassels]').dataset.mtoTassels);
            const picker = this.sizePickers.get(actions);
            if (picker?.open) { picker.stageOption(index, radio.value); return; }
            const values = this.variantOptionValues.get(this.selectedVariant?.value);
            if (!picker || !values) return;
            const matches = this.sizeInputs.filter((input) => {
              const tuple = this.variantOptionValues.get(input.value);
              return tuple.every((value, position) => value === (position === index ? radio.value : values[position]));
            });
            if (matches.length === 1) { picker.close(); matches[0].checked = true; this.selectSize(matches[0], { commitPrice: false }); }
            else picker.stageOption(index, radio.value);
          });
        });
        actions.querySelectorAll('[data-mto-native-option]').forEach((field) => {
          field.addEventListener('change', () => this.sizePickers.get(actions)?.stageOption(Number(field.dataset.mtoNativeOption), field.value));
        });
      }

      mountStickyActions() {
        this.sticky = this.querySelector('[data-mto-sticky]');
        if (!this.sticky || this.stickyBound) return;
        this.stickyBound = true;
        if (this.boundStickyElement !== this.sticky) {
          this.boundStickyElement = this.sticky;
          this.sticky.querySelector('[data-mto-sticky-edit]').addEventListener('click', () => this.focusInlineChooser());
          this.sticky.querySelector('[data-mto-sticky-add]').addEventListener('click', () => {
            this.state.payment = 'full';
            this.addFullPaymentToCart();
          });
        }
        this.stickyObserver = new IntersectionObserver(() => this.syncSticky());
        this.actionInstances.forEach((actions) => this.stickyObserver.observe(actions));
        document.querySelectorAll('.vpd-trade-enquiry, #vn19-site-footer, footer').forEach((region) => this.stickyObserver.observe(region));
        this.stickyStateObserver = new MutationObserver(() => this.syncSticky());
        this.stickyStateObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        const cart = document.querySelector('cart-drawer');
        if (cart) this.stickyStateObserver.observe(cart, { attributes: true, attributeFilter: ['class'] });
        const menu = document.querySelector('[data-vorug-native-menu]');
        if (menu) this.stickyStateObserver.observe(menu, { attributes: true, subtree: true, attributeFilter: ['class', 'hidden', 'aria-expanded'] });
      }

      syncSticky() {
        if (!this.sticky) return;
        const chooser = this.actionInstances?.find((actions) => actions.getClientRects().length > 0);
        const passedChooser = chooser && chooser.getBoundingClientRect().bottom <= 0;
        const otherOverlay = document.querySelector('cart-drawer.active, #vn19-mobile-drawer:not([hidden]), [data-vorug-native-menu] .mega:not([hidden]), [data-vorug-native-menu] .popover:not([hidden]), .menu-drawer-container[open], .mto-select.is-open');
        const bodyLocked = document.body.classList.contains('vn19-body-locked') || document.body.classList.contains('hidden');
        const readingEnquiryOrFooter = Array.from(document.querySelectorAll('.vpd-trade-enquiry, #vn19-site-footer, footer')).some((region) => {
          const bounds = region.getBoundingClientRect();
          return bounds.top < window.innerHeight && bounds.bottom > 0;
        });
        this.sticky.hidden = !passedChooser || this.isOpen || bodyLocked || !!otherOverlay || readingEnquiryOrFooter;
        const readingGallery = !!document.querySelector('[data-vorug-product-media][data-pin-active="true"]');
        this.sticky.classList.toggle('is-gallery-hidden', readingGallery);
        this.sticky.inert = readingGallery;
        if (readingGallery) this.sticky.setAttribute('aria-hidden', 'true');
        else this.sticky.removeAttribute('aria-hidden');
        const variant = this.state.selectionMode === 'preset' ? this.selectedVariant : null;
        const parts = [variant ? this.displaySizeLabel(variant.dataset.variantTitle) : this.copy.choose_size];
        ['shape', 'color'].forEach((kind) => {
          const current = chooser?.querySelector(`[data-vorug-family-kind="${kind}"][aria-current="true"]`);
          if (current) parts.push(current.getAttribute('aria-label'));
        });
        this.sticky.querySelector('[data-mto-sticky-summary]').textContent = parts.filter(Boolean).join(' | ');
        this.sticky.querySelector('[data-mto-sticky-price]').textContent = variant?.dataset.price || '';
        const add = this.sticky.querySelector('[data-mto-sticky-add]');
        add.disabled = !this.canPurchase() || !!this.cartPending;
        add.querySelector('span').textContent = this.cartPending ? this.copy.adding : this.copy.add_to_cart;
        if (this.cartPending) add.setAttribute('aria-busy', 'true');
        else add.removeAttribute('aria-busy');
      }

      enhanceSizeSelect(actions) {
        if (this.sizePickers.has(actions)) return this.sizePickers.get(actions);
        const select = actions.querySelector('[data-mto-inline-size]');
        const wrapper = actions.querySelector('[data-mto-select]');
        const trigger = actions.querySelector('[data-mto-size-trigger]');
        const menu = actions.querySelector('[data-mto-size-menu]');
        const options = actions.querySelector('[data-mto-size-options]');
        const confirm = actions.querySelector('[data-mto-size-confirm]');
        if (!select || !wrapper || !trigger || !menu || !options || !confirm) return;

        const nativeOptions = Array.from(actions.querySelectorAll('[data-mto-native-option]'));
        const mobile = matchMedia('(max-width: 749px)');
        const picker = { select, wrapper, trigger, menu, options, confirm, confirmLabel: confirm.textContent, nativeOptions, rows: [], activeIndex: -1, pendingValue: null, pendingSize: null, pendingOptions: null, outsideClicks: 0, open: false, sheet: false, customMode: false, quickUnit: this.state.unit, quickMm: { width: null, length: null }, quickValues: new Map(), quickRenderedValues: new Map(), quickInputs: new Map(), quickLabels: new Map() };
        const footer = confirm.parentElement;
        const customRow = document.createElement('div');
        customRow.className = 'mto-select-custom';
        const customButton = document.createElement('button');
        customButton.type = 'button';
        customButton.className = 'mto-select-option mto-select-custom-tile';
        customButton.id = `${options.id}-custom`;
        customButton.dataset.customSize = 'true';
        customButton.setAttribute('role', 'option');
        customButton.setAttribute('aria-selected', 'false');
        customButton.tabIndex = -1;
        const customTileLabel = document.createElement('span');
        customTileLabel.className = 'mto-select-option__size';
        customTileLabel.textContent = 'Custom size? +';
        customButton.append(customTileLabel);
        const customHeading = document.createElement('p');
        customHeading.className = 'mto-select-custom__heading';
        customHeading.textContent = 'Custom size:';
        customHeading.hidden = true;
        customRow.append(customHeading);
        picker.customHeading = customHeading;
        const customHelp = document.createElement('p');
        customHelp.className = 'mto-select-custom-help';
        customHelp.id = `${menu.id}-custom-help`;
        customHelp.textContent = this.copy.custom_size_help;
        const customError = document.createElement('p');
        customError.className = 'mto-select-custom__error';
        customError.id = `${menu.id}-custom-error`;
        customError.setAttribute('role', 'status');
        customError.setAttribute('aria-live', 'polite');
        ['width', 'length'].forEach((axis) => {
          const label = document.createElement('label');
          label.className = 'mto-select-custom__field';
          const text = document.createElement('span');
          const input = document.createElement('input');
          input.id = `${menu.id}-custom-${axis}`;
          input.type = 'number';
          input.inputMode = 'decimal';
          input.step = 'any';
          input.min = '0';
          input.autocomplete = 'off';
          input.setAttribute('aria-describedby', `${customHelp.id} ${customError.id}`);
          label.htmlFor = input.id;
          const unit = document.createElement('span');
          unit.className = 'mto-select-custom__unit';
          unit.setAttribute('aria-hidden', 'true');
          unit.textContent = this.state.unit === 'm' ? 'cm' : 'ft';
          const fieldBox = document.createElement('span');
          fieldBox.className = 'mto-select-custom__input-wrap';
          fieldBox.append(input, unit);
          label.append(text, fieldBox);
          customRow.append(label);
          picker.quickInputs.set(axis, input);
          picker.quickLabels.set(axis, text);
        });
        footer.insertBefore(customRow, confirm);
        footer.insertBefore(customHelp, confirm);
        footer.insertBefore(customError, confirm);
        picker.customRow = customRow;
        picker.customHelp = customHelp;
        picker.customButton = customButton;
        picker.customError = customError;

        const header = document.createElement('div');
        header.className = 'mto-size-sheet-header';
        const title = document.createElement('h2');
        title.id = `${menu.id}-title`;
        title.textContent = this.copy.choose_size;
        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'mto-size-sheet-close';
        closeButton.textContent = this.querySelector('[data-mto-close]').getAttribute('aria-label') || 'Close';
        const sheetUnits = actions.querySelector('.mto-unit-control').cloneNode(true);
        sheetUnits.querySelectorAll('[data-mto-unit]').forEach((button) => {
          button.dataset.mtoPickerUnit = button.dataset.mtoUnit;
          button.removeAttribute('data-mto-unit');
          button.addEventListener('click', () => this.setUnit(button.dataset.mtoPickerUnit));
        });
        const heading = document.createElement('div');
        heading.className = 'mto-size-sheet-heading';
        heading.append(title, closeButton);
        header.append(heading, sheetUnits);
        menu.prepend(header);
        picker.sheetUnits = sheetUnits;
        options.replaceChildren();
        Array.from(select.options).filter((option) => option.value).forEach((option) => {
          if (picker.rows.some((row) => row.dataset.sizeValue === option.dataset.sizeFt)) return;
          const row = document.createElement('div');
          row.className = 'mto-select-option';
          row.id = `${options.id}-option-${option.value}`;
          row.setAttribute('role', 'option');
          row.setAttribute('aria-selected', 'false');
          row.dataset.value = option.value;
          row.dataset.sizeValue = option.dataset.sizeFt;
          ['size', 'state'].forEach((part) => {
            const text = document.createElement('span');
            text.className = `mto-select-option__${part}`;
            row.append(text);
          });
          row.addEventListener('pointerdown', (event) => event.preventDefault());
          row.addEventListener('click', () => {
            stage(picker.rows.indexOf(row));
            commit();
          });
          picker.rows.push(row);
          options.append(row);
        });

        picker.rows.push(customButton);
        options.append(customButton);

        const activate = (index, scroll = true) => {
          if (!picker.rows.length) return;
          picker.activeIndex = Math.max(0, Math.min(picker.rows.length - 1, index));
          picker.rows.forEach((row, rowIndex) => row.classList.toggle('is-active', rowIndex === picker.activeIndex));
          const active = picker.rows[picker.activeIndex];
          trigger.setAttribute('aria-activedescendant', active.id);
          if (picker.sheet) options.setAttribute('aria-activedescendant', active.id);
          if (scroll) {
            const top = active.offsetTop;
            const bottom = top + active.offsetHeight;
            if (top < options.scrollTop) options.scrollTop = top;
            else if (bottom > options.scrollTop + options.clientHeight) options.scrollTop = bottom - options.clientHeight;
          }
        };
        const resetOutsideState = () => {
          picker.outsideClicks = 0;
          confirm.classList.remove('is-select-hint');
        };
        const stage = (index) => {
          if (select.disabled || picker.quotePending || !picker.rows.length) return;
          resetOutsideState();
          activate(index);
          picker.customMode = picker.rows[picker.activeIndex].dataset.customSize === 'true';
          picker.customError.textContent = '';
          picker.quickInputs.forEach((input) => input.removeAttribute('aria-invalid'));
          if (!picker.customMode) picker.pendingSize = picker.rows[picker.activeIndex].dataset.sizeValue;
          this.syncSizePicker(actions);
        };
        const restoreNode = (node, marker) => {
          if (node && marker?.parentNode) marker.replaceWith(node);
        };
        const updateSheetBounds = () => {
          if (!picker.sheet) return;
          const viewport = window.visualViewport;
          const height = window.vorugViewport?.height ?? viewport?.height ?? window.innerHeight;
          picker.layer.style.top = `${window.vorugViewport?.top ?? viewport?.offsetTop ?? 0}px`;
          picker.layer.style.height = `${height}px`;
          // A soft keyboard can leave less than the former 180px minimum.
          menu.style.maxHeight = `${Math.max(0, height - 8)}px`;
          menu.classList.toggle('is-compact-viewport', height < 440);
        };
        const leaveSheet = () => {
          if (!picker.sheet) return;
          picker.sheet = false;
          picker.backgroundObserver?.disconnect();
          document.removeEventListener('focusin', containFocus);
          document.removeEventListener('vorug:viewport-change', updateSheetBounds);
          window.visualViewport?.removeEventListener('resize', updateSheetBounds);
          window.visualViewport?.removeEventListener('scroll', updateSheetBounds);
          picker.background.forEach((attributes, node) => {
            ['inert', 'aria-hidden'].forEach((name) => {
              if (attributes[name] === null) node.removeAttribute(name);
              else node.setAttribute(name, attributes[name]);
            });
          });
          picker.background.clear();
          restoreNode(menu, picker.menuMarker);
          restoreNode(picker.nativeGroup, picker.nativeMarker);
          picker.layer.remove();
          menu.classList.remove('mto-select-menu--sheet', 'is-sheet-open', 'is-compact-viewport');
          menu.removeAttribute('role');
          menu.removeAttribute('aria-modal');
          menu.removeAttribute('aria-labelledby');
          options.removeAttribute('tabindex');
          options.removeAttribute('aria-activedescendant');
          options.removeAttribute('aria-label');
          options.setAttribute('aria-labelledby', actions.querySelector('.mto-inline-size-heading label').id);
          trigger.setAttribute('aria-haspopup', 'listbox');
          trigger.setAttribute('aria-controls', options.id);
          document.body.classList.remove('mto-size-sheet-open');
          Object.entries(picker.bodyStyles).forEach(([name, value]) => { document.body.style[name] = value; });
          window.scrollTo(picker.scrollX, picker.scrollY);
        };
        const containFocus = (event) => {
          if (picker.sheet && !menu.contains(event.target)) options.focus({ preventScroll: true });
        };
        const enterSheet = () => {
          if (picker.sheet) return;
          picker.sheet = true;
          picker.menuMarker = document.createComment('Vorug size menu');
          menu.before(picker.menuMarker);
          picker.layer = document.createElement('div');
          picker.layer.className = 'mto-size-sheet-layer mto-inline';
          const backdrop = document.createElement('div');
          backdrop.className = 'mto-size-sheet-backdrop';
          backdrop.setAttribute('aria-hidden', 'true');
          picker.layer.append(backdrop, menu);
          document.body.append(picker.layer);
          picker.nativeGroup = actions.querySelector('.mto-inline-native-options');
          if (picker.nativeGroup) {
            picker.nativeMarker = document.createComment('Vorug native choices');
            picker.nativeGroup.before(picker.nativeMarker);
            options.after(picker.nativeGroup);
          }
          menu.classList.add('mto-select-menu--sheet');
          menu.setAttribute('role', 'dialog');
          menu.setAttribute('aria-modal', 'true');
          menu.setAttribute('aria-labelledby', title.id);
          options.tabIndex = 0;
          options.removeAttribute('aria-labelledby');
          options.setAttribute('aria-label', actions.querySelector('.mto-inline-size-heading label').textContent);
          trigger.setAttribute('aria-haspopup', 'dialog');
          trigger.setAttribute('aria-controls', menu.id);
          picker.background = new Map();
          const makeBackgroundInert = () => Array.from(document.body.children).forEach((node) => {
            if (node === picker.layer || picker.background.has(node)) return;
            picker.background.set(node, { inert: node.getAttribute('inert'), 'aria-hidden': node.getAttribute('aria-hidden') });
            node.setAttribute('inert', '');
            node.setAttribute('aria-hidden', 'true');
          });
          makeBackgroundInert();
          picker.backgroundObserver = new MutationObserver(makeBackgroundInert);
          picker.backgroundObserver.observe(document.body, { childList: true });
          picker.scrollX = window.scrollX;
          picker.scrollY = window.scrollY;
          picker.bodyStyles = Object.fromEntries(['position', 'top', 'left', 'right', 'width', 'overflow'].map((name) => [name, document.body.style[name]]));
          Object.assign(document.body.style, { position: 'fixed', top: `${-picker.scrollY}px`, left: `${-picker.scrollX}px`, right: '0', width: '100%', overflow: 'hidden' });
          document.body.classList.add('mto-size-sheet-open');
          document.addEventListener('focusin', containFocus);
          document.addEventListener('vorug:viewport-change', updateSheetBounds);
          window.visualViewport?.addEventListener('resize', updateSheetBounds);
          window.visualViewport?.addEventListener('scroll', updateSheetBounds);
          updateSheetBounds();
          requestAnimationFrame(() => {
            if (!picker.sheet || !picker.open) return;
            menu.classList.add('is-sheet-open');
            options.focus({ preventScroll: true });
          });
        };
        const close = ({ preservePending = false, restoreFocus = false } = {}) => {
          const wasSheet = picker.sheet;
          picker.open = false;
          if (!preservePending) { picker.pendingValue = null; picker.pendingSize = null; picker.pendingOptions = null; }
          resetOutsideState();
          picker.search = '';
          picker.searchAt = 0;
          leaveSheet();
          menu.hidden = true;
          menu.style.maxHeight = '';
          trigger.setAttribute('aria-expanded', 'false');
          trigger.removeAttribute('aria-activedescendant');
          wrapper.classList.remove('is-open');
          if ((wasSheet || restoreFocus) && trigger.isConnected) trigger.focus({ preventScroll: true });
        };
        const positionMenu = () => {
          if (!picker.open || mobile.matches) { menu.style.removeProperty('--mto-menu-available'); menu.removeAttribute('data-placement'); return; }
          const viewport = window.vorugViewport;
          const bottom = viewport ? viewport.top + viewport.height : window.innerHeight;
          const top = Math.max(viewport?.top || 0, document.getElementById('vn19-site-header')?.getBoundingClientRect().bottom || 0);
          const bounds = wrapper.getBoundingClientRect();
          const below = bottom - bounds.bottom - 8;
          const above = bounds.top - top - 8;
          const up = below < 180 && above > below;
          menu.dataset.placement = up ? 'up' : 'down';
          menu.style.setProperty('--mto-menu-available', `${Math.max(0, up ? above : below)}px`);
        };
        const open = (edge = null) => {
          if (select.disabled || !picker.rows.length || picker.open) return;
          this.sizePickers.forEach((other) => { if (other !== picker) other.close({ preservePending: true }); });
          picker.open = true;
          resetOutsideState();
          const selected = this.sizeInputs.find((input) => input.value === select.value);
          picker.pendingSize = picker.pendingSize || selected?.dataset.variantTitle || null;
          if (!picker.pendingOptions) picker.pendingOptions = new Map(nativeOptions.map((field) => [Number(field.dataset.mtoNativeOption), field.value]));
          menu.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
          wrapper.classList.add('is-open');
          menu.style.maxHeight = 'none';
          const current = picker.rows.findIndex((row) => row.dataset.sizeValue === picker.pendingSize);
          this.syncInline();
          positionMenu();
          activate(edge === 'end' ? picker.rows.length - 1 : edge === 'start' ? 0 : Math.max(0, current));
        };
        const commit = () => {
          if (picker.customMode) { reviewCustom(); return; }
          const variant = this.resolvePendingVariant(picker);
          if (select.disabled || !variant) return;
          const value = variant.value;
          // Selecting a complete standard variant commits it immediately.
          this.sizePickers.forEach((other) => other.close());
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          trigger.focus({ preventScroll: true });
        };
        const outside = (event) => {
          if (!picker.open || menu.contains(event.target)) return;
          const sizeControls = event.target.closest?.('.mto-inline-size, .mto-inline-native-options');
          if (sizeControls && actions.contains(sizeControls)) {
            resetOutsideState();
            return;
          }
          close({ preservePending: true }); this.syncInline();
        };
        const keydown = (event) => {
          if (select.disabled) return;
          if (event.key === 'Tab') return;
          if (event.key === 'Escape') {
            if (picker.open) event.stopPropagation();
            if (picker.open) { event.preventDefault(); close({ preservePending: true }); this.syncInline(); }
            return;
          }
          if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
            event.preventDefault();
            if (!picker.open) open();
            const now = Date.now();
            const previous = now - (picker.searchAt || 0) < 700 ? picker.search || '' : '';
            const character = event.key.toLocaleLowerCase();
            picker.search = previous && previous.split('').every((part) => part === character) ? character : previous + character;
            picker.searchAt = now;
            const start = picker.search.length === 1 ? picker.activeIndex + 1 : 0;
            for (let offset = 0; offset < picker.rows.length; offset += 1) {
              const index = (start + offset) % picker.rows.length;
              const text = picker.rows[index].querySelector('.mto-select-option__size').textContent.toLocaleLowerCase();
              if (text.startsWith(picker.search)) { stage(index); break; }
            }
            return;
          }
          if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown', 'Enter', ' '].includes(event.key)) return;
          event.preventDefault();
          if (!picker.open) { open(event.key === 'ArrowUp' || event.key === 'End' ? 'end' : event.key === 'Home' ? 'start' : null); return; }
          const columns = getComputedStyle(options).gridTemplateColumns.trim().split(/\s+/).length;
          if (event.key === 'ArrowDown') stage(picker.activeIndex + columns);
          else if (event.key === 'ArrowUp') stage(picker.activeIndex - columns);
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            const direction = getComputedStyle(options).direction === 'rtl' ? -1 : 1;
            stage(picker.activeIndex + (event.key === 'ArrowRight' ? direction : -direction));
          }
          else if (event.key === 'PageDown') stage(picker.activeIndex + 10);
          else if (event.key === 'PageUp') stage(picker.activeIndex - 10);
          else if (event.key === 'Home') stage(0);
          else if (event.key === 'End') stage(picker.rows.length - 1);
          else { stage(picker.activeIndex); if (picker.customMode) picker.quickInputs.values().next().value?.focus(); else commit(); }
        };
        const modalKeydown = (event) => {
          if (!picker.sheet) return;
          if (event.key === 'Escape') {
            event.preventDefault(); event.stopPropagation(); close({ preservePending: true }); this.syncInline(); return;
          }
          if (event.key !== 'Tab') return;
          const focusable = Array.from(menu.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex="0"]')).filter((node) => !node.closest('[hidden]') && node.getClientRects().length);
          const first = focusable[0]; const last = focusable[focusable.length - 1];
          if (!first) { event.preventDefault(); options.focus({ preventScroll: true }); return; }
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        };
        const reviewCustom = async () => {
          if (select.disabled || this.cartPending || !this.captureQuickCustom(picker, true)) return;
          const dimensions = { widthMm: picker.quickMm.width, lengthMm: picker.quickMm.length, unit: picker.quickUnit };
          this.openCustomStep(dimensions);
        };
        picker.quickInputs.forEach((input) => {
          input.addEventListener('focus', () => {
            picker.customMode = true;
            resetOutsideState();
            this.renderQuickCustom(picker);
          });
          input.addEventListener('input', () => {
            picker.customMode = true;
            resetOutsideState();
            this.captureQuickCustom(picker, false);
          });
          input.addEventListener('blur', () => {
            if (picker.customMode) this.captureQuickCustom(picker, false);
          });
          input.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            reviewCustom();
          });
        });
        customButton.addEventListener('click', () => {
          if (select.disabled || this.cartPending) return;
          stage(picker.rows.indexOf(customButton));
          picker.quickInputs.values().next().value?.focus();
        });
        closeButton.addEventListener('click', () => { close({ preservePending: true, restoreFocus: true }); this.syncInline(); });
        const triggerClick = () => { resetOutsideState(); if (picker.open) { close({ preservePending: true }); this.syncInline(); } else open(); };
        trigger.addEventListener('click', triggerClick);
        trigger.addEventListener('keydown', keydown);
        options.addEventListener('keydown', keydown);
        menu.addEventListener('keydown', modalKeydown);
        confirm.addEventListener('click', commit);
        const label = actions.querySelector(`label[for="${select.id}"]`);
        const labelClick = (event) => { event.preventDefault(); trigger.focus(); };
        label?.addEventListener('click', labelClick);
        document.addEventListener('click', outside);
        const resize = () => {
          if (!picker.open) return;
          if (picker.sheet) leaveSheet();
          menu.style.maxHeight = 'none';
          updateSheetBounds();
          positionMenu();
          activate(picker.activeIndex);
        };
        mobile.addEventListener('change', resize);
        window.addEventListener('resize', resize);
        window.addEventListener('scroll', positionMenu, { passive: true, capture: true });
        document.addEventListener('vorug:viewport-change', positionMenu);
        picker.stageOption = (index, value) => {
          if (select.disabled) return;
          if (!picker.pendingOptions) picker.pendingOptions = new Map(nativeOptions.map((field) => [Number(field.dataset.mtoNativeOption), field.value]));
          picker.pendingOptions.set(index, value);
          resetOutsideState(); open(); this.syncInline();
          if (!picker.customMode && this.resolvePendingVariant(picker)) commit();
        };
        picker.resetOutsideState = resetOutsideState;
        picker.close = close;
        picker.destroy = () => {
          close(); document.removeEventListener('click', outside); mobile.removeEventListener('change', resize); window.removeEventListener('resize', resize); window.removeEventListener('scroll', positionMenu, true); document.removeEventListener('vorug:viewport-change', positionMenu);
          trigger.removeEventListener('click', triggerClick); trigger.removeEventListener('keydown', keydown); options.removeEventListener('keydown', keydown); menu.removeEventListener('keydown', modalKeydown); confirm.removeEventListener('click', commit); label?.removeEventListener('click', labelClick);
          confirm.textContent = picker.confirmLabel;
          customButton.remove(); customRow.remove(); customHelp.remove(); customError.remove(); header.remove(); wrapper.removeAttribute('data-enhanced'); select.hidden = false; trigger.hidden = true;
        };
        this.sizePickers.set(actions, picker);
        wrapper.dataset.enhanced = 'true';
        select.hidden = true;
        trigger.hidden = false;
        this.syncSizePicker(actions);
      }

      customDimensionLimits() {
        // Tweed accepts positive enquiry dimensions; direct ordering has its own verified envelope.
        if (this.dataset.productId === '10453653946648') return {
          length: { min: 1, max: Number.MAX_SAFE_INTEGER, label: 'a positive length' },
          width: { min: 1, max: Number.MAX_SAFE_INTEGER, label: 'a positive width' }
        };
        return {
          length: { min: 1524, max: 14504, label: "5' to 47'7\"" },
          width: { min: 914, max: 10490, label: "3' to 34'5\"" }
        };
      }

      captureQuickCustom(picker, showErrors) {
        const limits = this.customDimensionLimits();
        const messages = [];
        const showRequiredErrors = showErrors || Array.from(picker.quickInputs.values()).some((input) => input.hasAttribute('aria-invalid'));
        let valid = true;
        picker.quickInputs.forEach((input, axis) => {
          const raw = input.value.trim();
          picker.quickValues.set(`${picker.quickUnit}-${axis}`, raw);
          const numeric = Number(raw);
          const mm = raw && Number.isFinite(numeric) && numeric > 0 ? Math.round(numeric * (picker.quickUnit === 'm' ? 10 : 304.8)) : null;
          if (input.validity.badInput || raw !== picker.quickRenderedValues.get(axis) || !Number.isFinite(picker.quickMm[axis])) picker.quickMm[axis] = mm;
          const canonicalMm = picker.quickMm[axis];
          let message = '';
          if (!Number.isSafeInteger(canonicalMm) || canonicalMm <= 0) { valid = false; if (showRequiredErrors || raw || input.validity.badInput) message = `Enter a valid ${axis} in ${picker.quickUnit === 'm' ? 'centimetres' : 'feet'}.`; }
          else if (canonicalMm < limits[axis].min || canonicalMm > limits[axis].max) {
            valid = false;
            const scale = picker.quickUnit === 'm' ? 10 : 304.8;
            const range = `${this.trimDecimal((limits[axis].min / scale).toFixed(3))} to ${this.trimDecimal((limits[axis].max / scale).toFixed(3))} ${picker.quickUnit === 'm' ? 'cm' : 'ft'}`;
            message = `${axis[0].toUpperCase()}${axis.slice(1)} must be ${range}.`;
          }
          picker.quickRenderedValues.set(axis, raw);
          if (message) { input.setAttribute('aria-invalid', 'true'); messages.push(message); }
          else input.removeAttribute('aria-invalid');
        });
        picker.customError.textContent = messages.join(' ');
        if (showErrors && !valid) Array.from(picker.quickInputs.values()).find((input) => input.hasAttribute('aria-invalid'))?.focus();
        this.renderQuickCustom(picker);
        return valid;
      }

      renderQuickCustom(picker) {
        const custom = !!picker.customMode;
        picker.customRow.classList.toggle('is-custom-active', custom);
        picker.menu.classList.toggle('is-custom-preview', custom);
        picker.customButton.querySelector('.mto-select-option__size').textContent = custom ? 'Custom size −' : 'Custom size? +';
        picker.customButton.setAttribute('aria-selected', String(custom));
        picker.customButton.classList.toggle('is-pending', custom);
        picker.customHeading.hidden = true;
        picker.customRow.hidden = !custom;
        picker.quickInputs.forEach((input) => { input.closest('label').hidden = !custom; });
        picker.customHelp.hidden = !custom;
        const limits = this.customDimensionLimits();
        const dimensionsValid = ['width', 'length'].every((axis) => Number.isSafeInteger(picker.quickMm[axis]) && picker.quickMm[axis] >= limits[axis].min && picker.quickMm[axis] <= limits[axis].max);
        const pending = custom ? null : this.resolvePendingVariant(picker);
        picker.confirm.disabled = picker.select.disabled || !!this.cartPending || (custom ? !dimensionsValid : !pending);
        this.syncPendingTotal(picker, pending);
        picker.confirm.textContent = custom ? this.copy.request_custom_size : (pending ? picker.confirmLabel : this.copy.choose_size);
        picker.confirm.hidden = !custom;
      }

      syncQuickCustom(picker) {
        if (picker.quickUnit !== this.state.unit) {
          this.captureQuickCustom(picker, false);
          picker.quickUnit = this.state.unit;
          picker.quickInputs.forEach((input, axis) => {
            const mm = picker.quickMm[axis];
            const scale = this.state.unit === 'm' ? 10 : 304.8;
            input.value = Number.isFinite(mm) ? this.trimDecimal((mm / scale).toFixed(3)) : picker.quickValues.get(`${this.state.unit}-${axis}`) || '';
            picker.quickRenderedValues.set(axis, input.value);
          });
          picker.customError.textContent = '';
          picker.quickInputs.forEach((input) => input.removeAttribute('aria-invalid'));
        }
        picker.customRow.querySelectorAll('.mto-select-custom__unit').forEach((unit) => { unit.textContent = this.state.unit === 'm' ? 'cm' : 'ft'; });
        picker.quickLabels.forEach((label, axis) => {
          label.textContent = this.copy[`custom_${axis}`];
        });
        const limits = this.customDimensionLimits();
        const scale = this.state.unit === 'm' ? 10 : 304.8;
        picker.quickInputs.forEach((input, axis) => {
          const unitName = this.state.unit === 'm' ? 'centimetres' : 'feet';
          input.setAttribute('aria-label', `${this.copy[`custom_${axis}`]} (${unitName})`);
          input.placeholder = this.state.unit === 'm' ? 'cm' : this.copy[`custom_${axis}_ft_placeholder`];
          input.min = this.trimDecimal((limits[axis].min / scale).toFixed(3));
          input.max = this.trimDecimal((limits[axis].max / scale).toFixed(3));
          input.disabled = !!this.cartPending;
        });
        picker.customButton.disabled = !!this.cartPending;
        this.renderQuickCustom(picker);
        picker.sheetUnits.querySelectorAll('[data-mto-picker-unit]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.mtoPickerUnit === this.state.unit));
          button.disabled = !!this.cartPending;
        });
      }

      matchingSizeVariants(picker, sizeValue, override = null) {
        const selectedValues = picker.pendingOptions || new Map(picker.nativeOptions.map((field) => [Number(field.dataset.mtoNativeOption), field.value]));
        return this.sizeInputs.filter((input) => {
          const values = this.variantOptionValues.get(input.value);
          if (values[this.sizeOptionIndex] !== sizeValue) return false;
          return picker.nativeOptions.every((field) => {
            const index = Number(field.dataset.mtoNativeOption);
            const value = override?.index === index ? override.value : selectedValues.get(index);
            return !value || values[index] === value;
          });
        });
      }

      resolvePendingVariant(picker) {
        if (!picker.pendingSize || picker.nativeOptions.some((field) => !picker.pendingOptions?.get(Number(field.dataset.mtoNativeOption)))) return null;
        const matches = this.matchingSizeVariants(picker, picker.pendingSize);
        // Ambiguous combinations are never resolved by taking the first variant.
        return matches.length === 1 ? matches[0] : null;
      }

      hasNativeOptionDraft() {
        const committed = this.variantOptionValues.get(this.selectedVariant?.value);
        return Array.from(this.sizePickers.values()).some((picker) => picker.pendingOptions && picker.nativeOptions.some((field) => picker.pendingOptions.get(Number(field.dataset.mtoNativeOption)) !== committed?.[Number(field.dataset.mtoNativeOption)]));
      }

      syncSizePicker(actions) {
        const picker = this.sizePickers.get(actions);
        if (!picker) return;
        const { select, trigger, rows, confirm, nativeOptions } = picker;
        const selected = Array.from(select.options).find((option) => option.value && option.value === select.value);
        const label = this.customQuote ? this.customQuote.dimensionLabel : selected ? (this.displaySizeLabel(selected.dataset.sizeFt)) : this.copy.choose_size;
        trigger.querySelector('[data-mto-size-value]').textContent = label;
        trigger.disabled = select.disabled;
        if (select.disabled) picker.close();
        const committed = this.state.selectionMode === 'preset' ? this.variantOptionValues.get(this.selectedVariant?.value) : null;
        nativeOptions.forEach((field) => {
          const index = Number(field.dataset.mtoNativeOption);
          field.disabled = select.disabled;
          field.value = picker.pendingOptions ? picker.pendingOptions.get(index) || '' : committed?.[index] || '';
          const effectiveSize = picker.pendingSize || committed?.[this.sizeOptionIndex];
          const radios = field.closest('.mto-inline-native-option').querySelectorAll('[data-mto-tassel-radio]');
          radios.forEach((radio) => {
            radio.checked = radio.value === field.value || !!(radio.dataset.mtoTasselAlias && radio.dataset.mtoTasselAlias === field.value);
            radio.disabled = select.disabled || !!(effectiveSize && this.matchingSizeVariants(picker, effectiveSize, { index, value: radio.value }).length !== 1);
          });
          Array.from(field.options).forEach((option) => {
            option.disabled = !!(option.value && effectiveSize && this.matchingSizeVariants(picker, effectiveSize, { index, value: option.value }).length !== 1);
          });
        });
        const pending = this.resolvePendingVariant(picker);
        const orderAvailable = actions.querySelector('[data-mto-order-available], .mto-inline-order-available');
        if (orderAvailable) orderAvailable.hidden = !this.nativeOrderAvailable(actions);
        picker.pendingValue = pending?.value || null;
        confirm.disabled = select.disabled || !pending;
        confirm.textContent = pending ? picker.confirmLabel : this.copy.choose_size;
        this.syncQuickCustom(picker);
        const status = picker.menu.querySelector('[data-mto-option-status]');
        if (status) {
          const incomplete = nativeOptions.some((field) => !picker.pendingOptions?.get(Number(field.dataset.mtoNativeOption)));
          status.textContent = picker.open && picker.pendingSize && !pending ? (incomplete ? this.copy.choose_options_status : this.copy.unavailable_combination) : '';
        }
        rows.forEach((row) => {
          if (row.dataset.customSize === 'true') return;
          const matches = this.matchingSizeVariants(picker, row.dataset.sizeValue);
          const unavailable = !matches.some((input) => !input.disabled);
          const sizeLabel = this.displaySizeLabel(row.dataset.sizeValue);
          const availability = unavailable ? this.copy.unavailable : this.copy.available;
          row.querySelector('.mto-select-option__size').textContent = sizeLabel;
          row.querySelector('.mto-select-option__state').textContent = availability;
          row.setAttribute('aria-label', `${sizeLabel}, ${availability}`);
          row.dataset.unavailable = String(unavailable);
          row.setAttribute('aria-selected', String(!picker.customMode && row.dataset.sizeValue === (picker.open ? picker.pendingSize : selected?.dataset.sizeFt)));
          row.classList.toggle('is-pending', picker.open && !picker.customMode && row.dataset.sizeValue === picker.pendingSize);
        });
      }

      nativeOrderAvailable(actions) {
        const picker = this.sizePickers.get(actions);
        if (picker?.customMode && picker.open) return false;
        const variant = (picker?.open || picker?.pendingOptions) ? this.resolvePendingVariant(picker) : (this.state.selectionMode === 'preset' ? this.selectedVariant : null);
        return !!variant && !variant.disabled && Number(variant.dataset.priceCents) > 0;
      }

      syncPendingTotal(picker, pending) {
        const output = picker.menu.querySelector('[data-mto-pending-total]');
        if (!output) return;
        output.hidden = !picker.open;
        if (picker.customMode && !pending) {
          output.textContent = this.copy.price_request;
          output.setAttribute('aria-label', output.textContent);
          delete output.dataset.variantId;
          return;
        }
        const priced = Number(pending?.dataset.priceCents) > 0;
        output.textContent = !pending ? this.copy.choose_options_status : (!priced ? this.copy.price_request : (pending.disabled ? this.copy.unavailable_status : pending.dataset.price));
        output.setAttribute('aria-label', pending && priced && !pending.disabled ? `${this.copy.total || 'Total'} ${pending.dataset.price}` : output.textContent);
        if (pending) output.dataset.variantId = pending.value;
        else delete output.dataset.variantId;
      }

      canPurchase() {
        return !this.customAddChecking && !Array.from(this.sizePickers.values()).some((picker) => picker.open) && !this.hasNativeOptionDraft() && this.state.selectionMode === 'preset' && !!this.selectedVariant && !this.selectedVariant.disabled && Number(this.selectedVariant.dataset.priceCents) > 0;
      }

      clearInlineSelection() {
        this.priceSelectionCommitted = false;
        this.customMatchedDimensions = null;
        this.customQuote = null;
        this.customFlowActive = false;
        this.customReturnVariantId = null;
        this.selectedVariant = null;
        this.state.selectionMode = null;
        this.state.variantId = null;
        this.state.payment = null;
        this.sizeInputs.forEach((input) => { input.checked = false; });
        this.paymentInputs.forEach((input) => { input.checked = false; });
        this.nextButton.disabled = true;
        const input = this.nativeForm?.querySelector('[name="id"]');
        if (input) { input.value = ''; input.disabled = true; }
        const url = new URL(location.href);
        url.searchParams.delete('variant');
        history.replaceState(history.state, '', url);
        this.resetAddButton();
        this.syncInline();
        this.updateHeadingRate();
      }

      contactResponseBelongsHere() {
        if (new URLSearchParams(window.location.search).get('mto_request') === 'sent') return true;
        const formId = this.querySelector('form.mto-request-form')?.id;
        let responseHash;
        try { responseHash = decodeURIComponent(window.location.hash.slice(1)); } catch { return false; }
        return !!formId && responseHash === formId;
      }

      openHomepageChooser() {
        if (location.hash !== '#vorug-mto-chooser' || !this.actionInstances?.length || this.cartPending) return;
        if (this.isOpen) this.closeDrawer();
        this.focusInlineChooser();
        const chooser = this.actionInstances.find((actions) => actions.getClientRects().length > 0);
        const picker = this.sizePickers.get(chooser);
        if (picker && !picker.open) picker.trigger.click();
      }

      focusInlineChooser() {
        const chooser = this.actionInstances?.find((actions) => actions.getClientRects().length > 0);
        if (!chooser) return;
        if (this.isOpen) this.closeDrawer();
        const field = chooser.querySelector('[data-mto-size-trigger]:not([hidden])') || chooser.querySelector('[data-mto-inline-size]');
        field.focus({ preventScroll: true });
        chooser.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }

      syncInline() {
        (this.actionInstances || []).forEach((actions) => {
          const size = actions.querySelector('[data-mto-inline-size]');
          const orderAvailable = actions.querySelector('[data-mto-order-available], .mto-inline-order-available');
          if (orderAvailable) orderAvailable.hidden = !this.nativeOrderAvailable(actions);
          size.disabled = !!this.cartPending;
          actions.querySelector('[data-mto-inline-custom]').disabled = !!this.cartPending;
          if (actions.querySelector('[data-mto-color]')) actions.querySelector('[data-mto-color]').disabled = !!this.cartPending;
          size.value = this.state.selectionMode === 'preset' ? this.selectedVariant?.value || '' : '';
          Array.from(size.options).forEach((option) => {
            if (!option.dataset.sizeFt) return;
            const original = option.dataset.sizeFt;
            const label = this.displaySizeLabel(original);
            const input = this.sizeInputs.find((candidate) => candidate.value === option.value);
            option.textContent = `${label} · ${input?.disabled ? this.copy.unavailable : this.copy.available}`;
          });
          this.syncSizePicker(actions);
          actions.querySelector('[data-mto-inline-add]').disabled = !this.canPurchase() || !!this.cartPending;
          actions.querySelector('[data-mto-inline-payment]').disabled = !this.canPurchase() || !!this.cartPending;
          actions.querySelector('[data-mto-inline-total]').textContent = this.state.selectionMode === 'preset' && this.selectedVariant ? (Number(this.selectedVariant.dataset.priceCents) > 0 ? this.selectedVariant.dataset.price : this.copy.price_request) : this.copy.choose_size;
          actions.querySelector('[data-mto-inline-status]').textContent = this.state.selectionMode === 'preset' && this.selectedVariant ? (Number(this.selectedVariant.dataset.priceCents) <= 0 ? this.copy.price_request : (this.selectedVariant.disabled ? this.copy.unavailable_status : '')) : '';
          const sample = actions.querySelector('[data-vorug-sample-link]');
          if (sample) {
            const target = new URL(sample.href, location.origin);
            ['variant', 'size', 'price', 'return'].forEach((key) => target.searchParams.delete(key));
            if (this.state.selectionMode === 'preset' && Number(this.selectedVariant?.dataset.priceCents) > 0) {
              target.searchParams.set('variant', this.selectedVariant.value);
              target.searchParams.set('size', this.selectedVariant.dataset.variantTitle);
              target.searchParams.set('price', this.selectedVariant.dataset.price);
              const back = new URL(this.dataset.productUrl, location.origin);
              back.searchParams.set('variant', this.selectedVariant.value);
              back.hash = 'vorug-mto-chooser';
              target.searchParams.set('return', back.pathname + back.search + back.hash);
            }
            sample.href = target.pathname + target.search;
          }
          actions.querySelectorAll('[data-mto-unit]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.mtoUnit === this.state.unit)));
        });
        if (this.state.selectionMode === 'preset' && this.selectedVariant) {
          document.querySelectorAll('[data-vorug-variant-title], [data-mto-selection-size]').forEach(label => {
            label.textContent = this.displaySizeLabel(this.selectedVariant.dataset.variantTitle);
          });
        }
        this.syncSticky();
      }

      productionTimeValue() {
        if (this.state.selectionMode !== 'preset' || !this.selectedVariant) return '';
        const value = (this.selectedVariant.dataset.productionTime || '').trim();
        return ['3–5 weeks', '4–6 weeks', '6–10 weeks'].includes(value) ? value : '';
      }

      syncProductionTimeProperty() {
        if (!this.nativeForm) return;
        this.nativeForm.querySelectorAll('[name="properties[Production time]"]').forEach((input) => input.remove());
        let property = this.nativeForm.querySelector('[name="properties[_Production time]"]');
        const value = this.productionTimeValue();
        if (!value) {
          property?.remove();
          return;
        }
        if (!property) {
          property = document.createElement('input');
          property.type = 'hidden';
          property.name = 'properties[_Production time]';
          this.nativeForm.append(property);
        }
        property.value = value;
      }

      syncEnteredDimensionsProperties() {
        if (!this.nativeForm) return;
        const dimensions = this.customMatchedDimensions;
        const properties = dimensions ? {
          'Entered dimensions': `${this.trimDecimal((dimensions.widthMm / 10).toFixed(1))} X ${this.trimDecimal((dimensions.lengthMm / 10).toFixed(1))} cm`,
          '_vorug_dimensions_mm': `${dimensions.widthMm} × ${dimensions.lengthMm}`,
          '_vorug_dimension_unit': dimensions.unit,
          ...(this.customQuote?.properties || {}),
          ...(this.customAddIntent ? { '_vorug_add_intent': this.customAddIntent.intent } : {})
        } : {};
        ['Entered dimensions', '_vorug_dimensions_mm', '_vorug_dimension_unit', 'Custom dimensions', '_vorug_custom_quote', '_vorug_quote_signature', '_vorug_add_intent'].forEach((name) => {
          const fieldName = `properties[${name}]`;
          let input = this.nativeForm.querySelector(`[name="${fieldName}"]`);
          if (!properties[name]) { input?.remove(); return; }
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = fieldName;
            this.nativeForm.append(input);
          }
          input.value = properties[name];
        });
      }

      updateHeadingRate() {
        document.querySelectorAll('.main-product [data-mto-heading-rate]').forEach((output) => {
          output.textContent = this.priceSelectionCommitted && this.state.selectionMode === 'preset' && Number(this.selectedVariant?.dataset.priceCents) > 0 ? this.selectedVariant.dataset.price : output.dataset.fromPrice || this.copy.price_request;
        });
      }

      setCartStatus(message) {
        this.statusOutput.textContent = message;
        (this.actionInstances || []).forEach((actions) => { actions.querySelector('[data-mto-inline-status]').textContent = message; });
      }

      observeNativeCart() {
        const formOwner = this.nativeForm?.closest('product-form');
        if (!formOwner) return;
        const finish = (message, success = false) => {
          if (!this.cartPending) return;
          this.cartPending = false;
          this.addButton.removeAttribute('aria-busy');
          this.addButton.textContent = this.copy.add_to_cart;
          this.addButton.disabled = !this.canPurchase();
          this.syncInline();
          this.actionInstances.forEach((actions) => {
            const button = actions.querySelector('[data-mto-inline-add]');
            button.removeAttribute('aria-busy');
            button.querySelector('span').textContent = this.copy.add_to_cart;
          });
          this.setCartStatus(message);
          if (success && this.isOpen) this.closeDrawer();
        };
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
            if (String(event.productVariantId) !== String(this.selectedVariant?.value)) return;
            if (!this.customQuote) { finish(this.copy.cart_success, true); return; }
            this.customCartVerifying = true;
            this.releaseCustomCheckoutGuard = this.guardCustomCheckout();
            try {
              const cart = await this.readCustomCart();
              if (!this.customCartLine(cart, this.customAddIntent)) {
                await this.removeMismatchedCustomLine(cart, this.customAddIntent);
                this.clearCustomAddIntent();
                finish(this.copy.cart_error);
                return;
              }
              this.clearCustomAddIntent();
              finish(this.copy.cart_success, true);
            } catch (error) {
              finish(this.copy.custom_cart_pending || this.copy.cart_error);
            } finally { this.customCartVerifying = false; }
          });
          this.unsubscribeCartError = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
            if (String(event.productVariantId) === String(this.selectedVariant?.value)) {
              if (this.customQuote && Number(event.status) >= 400 && Number(event.status) < 500) this.clearCustomAddIntent();
              finish(event.message || this.copy.cart_error);
            }
          });
        }
        this.cartObserver = new MutationObserver(() => {
          if (!this.cartPending || this.customCartVerifying) return;
          const wrapper = formOwner.querySelector('.product-form__error-message-wrapper');
          if (wrapper && !wrapper.hidden && wrapper.textContent.trim()) finish(wrapper.textContent.trim());
          else if (!formOwner.querySelector('[type="submit"]')?.classList.contains('loading')) finish(this.customQuote ? this.copy.custom_cart_pending || this.copy.cart_error : this.copy.cart_error);
        });
        this.cartObserver.observe(formOwner, { attributes: true, childList: true, subtree: true, characterData: true });
      }

      openDrawer(options = {}) {
        if (!options.step && this.state.step === 'sizes') { this.focusInlineChooser(); return; }
        if (options.step) this.showStep(options.step);
        if (this.isOpen) return;

        this.track('pdp_size_drawer_open', {
          variant_id: this.selectedVariant?.value || null
        });

        window.clearTimeout(this.closeTimer);
        this.lastFocusedElement = document.activeElement;
        this.overlay.hidden = false;
        this.drawer.hidden = false;
        document.body.classList.add('mto-drawer-open');
        this.isOpen = true;
        this.syncSticky();

        this.openFrame = requestAnimationFrame(() => {
          this.openFrame = null;
          this.overlay.classList.add('is-open');
          this.drawer.classList.add('is-open');
          this.drawer.querySelector('[data-mto-close]').focus();
        });
      }

      closeDrawer() {
        if (!this.isOpen) return;

        if (this.customFlowActive && this.state.step !== 'success') {
          const previous = this.sizeInputs.find((input) => input.value === String(this.customReturnVariantId));
          if (previous) this.selectSize(previous, { track: false, preserveDraft: true, customDimensions: this.customReturnDimensions });
          else this.clearInlineSelection();
        }
        this.customFlowActive = false;
        this.customReturnVariantId = null;

        cancelAnimationFrame(this.openFrame);
        this.openFrame = null;
        this.overlay.classList.remove('is-open');
        this.drawer.classList.remove('is-open');
        document.body.classList.remove('mto-drawer-open');
        this.isOpen = false;

        const hide = () => {
          this.overlay.hidden = true;
          this.drawer.hidden = true;
        };
        if (matchMedia('(prefers-reduced-motion: reduce)').matches) hide();
        else this.closeTimer = window.setTimeout(hide, 330);

        if (this.lastFocusedElement instanceof HTMLElement) this.lastFocusedElement.focus();
      }

      showStep(stepName) {
        this.state.step = stepName;
        this.steps.forEach((step) => {
          step.hidden = step.dataset.mtoStep !== stepName;
        });

        const heading = this.querySelector(`[data-mto-step="${stepName}"] .mto-drawer__title`);
        if (heading) {
          if (!heading.id) heading.id = `MtoStepTitle-${this.dataset.productId}-${stepName}`;
          this.drawer.setAttribute('aria-labelledby', heading.id);
          requestAnimationFrame(() => { if (this.isOpen && this.state.step === stepName) heading.focus(); });
        }
        this.updateDrawerContext();
      }

      selectSize(input, options = {}) {
        if (options.track !== false && options.commitPrice !== false) this.priceSelectionCommitted = true;
        this.customMatchedDimensions = options.customDimensions || null;
        this.customQuote = options.customQuote || null;
        if (!options.preserveDraft) this.sizePickers.forEach((picker) => picker.close());
        this.customFlowActive = false;
        this.customReturnVariantId = null;
        this.selectedVariant = input;
        this.state.selectionMode = 'preset';
        this.state.variantId = input.value;
        this.state.payment = null;
        this.nextButton.disabled = input.disabled;
        this.presetSubtotal.textContent = input.dataset.price;
        this.paymentSubtotal.textContent = input.dataset.price;
        this.fullPriceOutput.textContent = input.dataset.price;
        this.halfPriceOutputs.forEach((output) => {
          output.textContent = input.dataset.halfPrice;
        });
        this.updateSelectionSummary(input);

        const nativeVariantInput = this.nativeForm?.querySelector('input[name="id"]');
        if (nativeVariantInput) {
          nativeVariantInput.value = input.value;
          nativeVariantInput.disabled = input.disabled;
        }

        if (options.updateUrl !== false) {
          const nextUrl = new URL(window.location.href);
          nextUrl.searchParams.set('variant', input.value);
          window.history.replaceState(window.history.state, '', nextUrl);
        }

        if (options.track !== false) {
          this.track('pdp_size_selected', {
            variant_id: input.value,
            variant_title: input.dataset.variantTitle,
            price: input.dataset.price
          });
        }

        this.paymentInputs.forEach((paymentInput) => {
          paymentInput.checked = false;
        });
        this.resetAddButton();
        this.syncInline();
        this.updateHeadingRate();
        this.updateDrawerContext();
        this.syncSelectedMedia(input);
      }

      syncSelectedMedia(input) {
        const gallery = this.actionsMountedRoot?.querySelector('[id^="MediaGallery-"]');
        if (!gallery) return;
        if (this.variantMediaGallery !== gallery) {
          this.variantImageFade?.cleanup();
          this.variantMediaGallery?.removeEventListener('vorug:gallery-ready', this.syncVariantMediaWhenReady);
          this.variantMediaGallery = gallery;
          gallery.addEventListener('vorug:gallery-ready', this.syncVariantMediaWhenReady);
        }
        const mediaId = input.dataset.featuredMediaId;
        const source = mediaId && this.querySelector(`template[data-mto-finish-image="${mediaId}"]`)?.content.querySelector('img');
        if (!source) { gallery.dataset.vorugVariantMedia = 'missing-association'; return; }
        const controller = gallery._vorugGallery;
        if (!controller) { gallery.dataset.vorugVariantMedia = 'waiting-gallery'; return; }
        const base = Array.from(controller.list.children).find((slide) => slide.classList.contains('product__media-item'));
        const image = base?.querySelector('.product__media img[srcset]:not([data-mto-fade-outgoing])');
        if (!image) { gallery.dataset.vorugVariantMedia = 'missing-base-slot'; return; }
        const changed = image.getAttribute('src') !== source.getAttribute('src');
        const previousFade = this.variantImageFade;
        const keepOutgoing = changed && previousFade?.waiting && previousFade.image === image;
        const outgoing = changed && !window.matchMedia('(prefers-reduced-motion: reduce)').matches && image.animate
          ? (keepOutgoing ? previousFade.outgoing : (image.complete && image.naturalWidth ? image.cloneNode(true) : null))
          : null;
        if (changed) previousFade?.cleanup(Boolean(outgoing && keepOutgoing));
        if (outgoing) {
          outgoing.removeAttribute('id');
          outgoing.setAttribute('data-mto-fade-outgoing', '');
          outgoing.setAttribute('aria-hidden', 'true');
          outgoing.style.pointerEvents = 'none';
          outgoing.style.zIndex = '1';
          image.after(outgoing);
          const visibility = image.style.visibility;
          image.style.visibility = 'hidden';
          const fade = {
            image, outgoing, waiting: true,
            cleanup: (keep = false) => {
              if (fade.cleaned) return;
              fade.cleaned = true;
              image.removeEventListener('load', ready);
              image.removeEventListener('error', failed);
              fade.outgoingAnimation?.cancel();
              image.style.visibility = visibility;
              if (!keep) outgoing.remove();
              if (this.variantImageFade === fade) this.variantImageFade = null;
            }
          };
          const failed = () => {
            if (this.variantImageFade !== fade) return;
            ['src', 'srcset', 'width', 'height', 'alt'].forEach((name) => {
              const value = outgoing.getAttribute(name);
              if (value !== null) image.setAttribute(name, value);
              else image.removeAttribute(name);
            });
            fade.cleanup();
            gallery.dataset.vorugVariantMedia = 'image-load-failed';
          };
          const ready = () => {
            if (fade.decoding) return;
            fade.decoding = true;
            const decoded = image.decode ? image.decode().catch(() => {}) : Promise.resolve();
            decoded.then(() => {
              if (this.variantImageFade !== fade || !image.isConnected || !image.naturalWidth) return;
              fade.waiting = false;
              image.style.visibility = visibility;
              const timing = { duration: 200, easing: 'ease-out' };
              // Keep incoming fully opaque so overlapping rug colors stay stable.
              fade.outgoingAnimation = outgoing.animate([{ opacity: 1 }, { opacity: 0 }], timing);
              fade.outgoingAnimation.finished.then(() => fade.cleanup()).catch(() => {});
            });
          };
          this.variantImageFade = fade;
          image.addEventListener('load', ready, { once: true });
          image.addEventListener('error', failed, { once: true });
          fade.ready = ready;
        }
        const replaceImage = (target) => {
          if (!target) return;
          ['src', 'srcset', 'width', 'height', 'alt'].forEach((name) => {
            const value = source.getAttribute(name);
            if (value !== null) {
              if (target.getAttribute(name) !== value) target.setAttribute(name, value);
            } else target.removeAttribute(name);
          });
          target.loading = 'eager';
        };
        replaceImage(image);
        const baseId = base.dataset.productMediaId;
        replaceImage(gallery.querySelector(`[data-target="${base.dataset.mediaId}"] img[srcset]`));
        const modalSelector = base.querySelector('modal-opener')?.dataset.modal;
        const modal = modalSelector && document.querySelector(modalSelector);
        replaceImage(modal?.querySelector(`img.product-modal-image[data-media-id="${baseId}"]`));
        base.dataset.vorugFinishMediaId = mediaId;
        controller.refresh();
        controller.goTo(0, 'instant');
        gallery.dataset.vorugVariantMedia = 'requested-first-slot';
        if (outgoing && image.complete && image.naturalWidth) this.variantImageFade?.ready();
      }

      updateSelectionSummary(input) {
        (this.actionInstances || []).forEach((actions) => {
          const summary = actions.querySelector('[data-mto-selection]');
          const size = actions.querySelector('[data-mto-selection-size]');
          const price = actions.querySelector('[data-mto-selection-price]');
          const availability = actions.querySelector('[data-mto-selection-availability]');
          if (!summary || !size || !price || !availability) return;
          summary.hidden = false;
          size.textContent = this.displaySizeLabel(input.dataset.variantTitle);
          price.textContent = input.dataset.price;
          availability.hidden = !input.disabled;
        });
      }

      track(eventName, detail = {}) {
        const payload = {
          event: eventName,
          product_id: this.dataset.productId,
          product_title: this.dataset.productTitle,
          ...detail
        };
        document.dispatchEvent(new CustomEvent(`vorug:${eventName}`, { detail: payload }));
        if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
        try {
          void window.Shopify?.analytics?.publish?.(`vorug_${eventName}`, payload);
        } catch (error) {
          // Analytics must never interrupt the purchase flow.
        }
      }

      selectPayment(input) {
        this.state.payment = input.value;
        this.statusOutput.textContent = '';
        this.addButton.disabled = false;

        if (input.value === 'full') {
          this.addButton.textContent = 'Add to cart';
          return;
        }

        this.addButton.textContent = 'Continue';
      }

      resetAddButton() {
        this.addButton.disabled = true;
        this.addButton.textContent = 'Add to cart';
        this.statusOutput.textContent = '';
      }

      openCustomStep(dimensions = null) {
        if (this.cartPending) return;
        this.customReturnVariantId = this.selectedVariant?.value || null;
        this.customReturnDimensions = this.customMatchedDimensions || null;
        this.customFlowActive = true;
        this.sizePickers.forEach((picker) => { picker.customMode = false; picker.close(); });
        this.state.selectionMode = 'custom';
        this.state.payment = null;
        if (dimensions) {
          this.state.custom.widthMm = dimensions.widthMm;
          this.state.custom.lengthMm = dimensions.lengthMm;
        }
        this.paymentInputs.forEach((input) => { input.checked = false; });
        this.populateCustomInputsFromState();
        this.resetAddButton();
        this.validateCustom(false);
        this.syncInline();
        this.updateHeadingRate();
        if (dimensions && this.state.custom.valid) {
          this.chooseCustomerType('personal');
          this.openDrawer({ step: 'request' });
        } else this.openDrawer({ step: 'custom' });
      }

      setUnit(unit, options = {}) {
        const nextUnit = unit === 'm' ? 'm' : 'ft';
        const preserveCustom = options.preserveCustom !== false;
        this.sizePickers.forEach((picker) => picker.resetOutsideState());

        if (preserveCustom) this.captureCustomValues(false);
        this.state.unit = nextUnit;
        try {
          localStorage.setItem(this.unitStorageKey, nextUnit);
        } catch (error) {
          // The switch still works when storage is unavailable.
        }

        this.unitButtons.forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.mtoUnit === nextUnit));
        });

        this.sizeLabels.forEach((label) => {
          const original = label.dataset.sizeFt;
          label.textContent = this.displaySizeLabel(original);
        });

        this.customFieldGroups.forEach((group) => {
          const active = group.dataset.mtoCustomFields === nextUnit;
          group.hidden = !active;
          group.querySelectorAll('input').forEach((input) => {
            input.disabled = !active;
          });
        });

        this.populateCustomInputsFromState();
        this.validateCustom(false);
        this.syncInline();
        this.updateDrawerContext();
        document.dispatchEvent(new CustomEvent('vorug:size-unit-change', { detail: { unit: nextUnit } }));
      }

      displaySizeLabel(label) {
        return this.state.unit === 'm' ? this.metricLabel(label) : String(label).replace(/[’′]/g, "'").replace(/\s*[x×]\s*/gi, ' × ');
      }

      metricLabel(label) {
        const parts = String(label).split(/\s*[x×]\s*/i);
        const diameter = String(label).match(/^(\d+(?:\.\d+)?)[’′'] diameter$/);
        if (diameter) return `${this.trimDecimal((Number(diameter[1]) * 30.48).toFixed(1))} cm diameter`;
        if (parts.length !== 2) return label;
        const first = this.dimensionPartToMeters(parts[0]);
        const second = this.dimensionPartToMeters(parts[1]);
        if (first === null || second === null) return label;
        return `${this.trimDecimal((first * 100).toFixed(1))} × ${this.trimDecimal((second * 100).toFixed(1))} cm`;
      }

      dimensionPartToMeters(part) {
        const match = String(part).trim().match(/^(\d+(?:\.\d+)?)\s*(?:['’′])?\s*(?:(\d+)\s*(?:["”″]))?$/);
        if (!match) return null;
        const feet = Number(match[1]);
        const inches = Number(match[2] || 0);
        if (!Number.isFinite(feet) || !Number.isFinite(inches) || inches > 11) return null;
        return (feet * 12 + inches) * 0.0254;
      }

      trimDecimal(value) {
        return String(value).replace(/(\.\d*?[1-9])0+$|\.0+$/, '$1');
      }

      captureCustomValues(showErrors) {
        const values = {};
        ['length', 'width'].forEach((axis) => {
          const parsed = this.readCustomDimension(axis, showErrors);
          const names = this.state.unit === 'm' ? [`${axis}-m`] : [`${axis}-ft`, `${axis}-in`];
          const edited = names.some((name) => this.customInputs.get(name).value !== this.customDisplayValues.get(name));
          const key = `${axis}Mm`;
          if (edited || !Number.isFinite(this.state.custom[key])) this.state.custom[key] = parsed.mm;
          names.forEach((name) => this.customDisplayValues.set(name, this.customInputs.get(name).value));
          values[axis] = { ...parsed, mm: this.state.custom[key] };
        });
        return values;
      }

      readCustomDimension(axis, showErrors) {
        if (this.state.unit === 'm') {
          const input = this.customInputs.get(`${axis}-m`);
          const raw = input.value.trim();
          if (raw === '') return { mm: null, message: showErrors ? `Enter a ${axis}.` : '' };
          const centimetres = Number(raw);
          if (!Number.isFinite(centimetres) || centimetres <= 0) return { mm: null, message: `Enter a valid ${axis} in centimetres.` };
          return { mm: Math.round(centimetres * 10), message: '' };
        }

        const feetInput = this.customInputs.get(`${axis}-ft`);
        const inchesInput = this.customInputs.get(`${axis}-in`);
        const feetRaw = feetInput.value.trim();
        const inchesRaw = inchesInput.value.trim();
        if (feetRaw === '' && inchesRaw === '') return { mm: null, message: showErrors ? `Enter a ${axis}.` : '' };
        if (!/^\d+$/.test(feetRaw || '0') || !/^\d+$/.test(inchesRaw || '0')) {
          return { mm: null, message: 'Use whole feet and inches.' };
        }
        const feet = Number(feetRaw || 0);
        const inches = Number(inchesRaw || 0);
        if (inches > 11) return { mm: null, message: 'Inches must be between 0 and 11.' };
        return { mm: Math.round((feet * 12 + inches) * 25.4), message: '' };
      }

      validateCustom(showErrors) {
        const values = this.captureCustomValues(showErrors);
        const limits = this.customDimensionLimits();

        ['length', 'width'].forEach((axis) => {
          const result = values[axis];
          let message = result.message;
          if (!message && result.mm !== null && (!Number.isSafeInteger(result.mm) || result.mm <= 0)) message = `Enter a valid ${axis}.`;
          if (!message && result.mm !== null && (result.mm < limits[axis].min || result.mm > limits[axis].max)) {
            const range = this.state.unit === 'm'
              ? `${this.trimDecimal((limits[axis].min / 10).toFixed(1))} to ${this.trimDecimal((limits[axis].max / 10).toFixed(1))} cm`
              : limits[axis].label;
            message = `${axis[0].toUpperCase()}${axis.slice(1)} must be ${range}.`;
          }
          this.setDimensionError(axis, message);
        });

        const lengthValid = Number.isSafeInteger(values.length.mm) && values.length.mm >= limits.length.min && values.length.mm <= limits.length.max;
        const widthValid = Number.isSafeInteger(values.width.mm) && values.width.mm >= limits.width.min && values.width.mm <= limits.width.max;
        this.state.custom.valid = lengthValid && widthValid;
        this.customNextButton.disabled = !this.state.custom.valid;
        this.updateDrawerContext();

        if (showErrors && !this.state.custom.valid) {
          const firstInvalid = ['length', 'width'].find((axis) => this.customErrors.get(axis).textContent);
          if (firstInvalid) {
            const suffix = this.state.unit === 'm' ? 'm' : 'ft';
            this.customInputs.get(`${firstInvalid}-${suffix}`)?.focus();
          }
        }
        return this.state.custom.valid;
      }

      setDimensionError(axis, message) {
        this.customErrors.get(axis).textContent = message;
        const names = this.state.unit === 'm' ? [`${axis}-m`] : [`${axis}-ft`, `${axis}-in`];
        names.forEach((name) => {
          const input = this.customInputs.get(name);
          if (!input) return;
          if (message) input.setAttribute('aria-invalid', 'true');
          else input.removeAttribute('aria-invalid');
        });
      }

      populateCustomInputsFromState() {
        ['length', 'width'].forEach((axis) => {
          const mm = this.state.custom[`${axis}Mm`];
          if (!Number.isFinite(mm)) {
            ['m', 'ft', 'in'].forEach((suffix) => {
              const name = `${axis}-${suffix}`;
              this.customInputs.get(name).value = '';
              this.customDisplayValues.set(name, '');
            });
            return;
          }

          if (this.state.unit === 'm') {
            const name = `${axis}-m`;
            const value = this.trimDecimal((mm / 10).toFixed(1));
            this.customInputs.get(name).value = value;
            this.customDisplayValues.set(name, value);
            return;
          }

          const totalInches = Math.round(mm / 25.4);
          const feet = String(Math.floor(totalInches / 12));
          const inches = String(totalInches % 12);
          this.customInputs.get(`${axis}-ft`).value = feet;
          this.customInputs.get(`${axis}-in`).value = inches;
          this.customDisplayValues.set(`${axis}-ft`, feet);
          this.customDisplayValues.set(`${axis}-in`, inches);
        });
      }

      updateDrawerContext() {
        let summary = '';
        if (this.state.selectionMode === 'custom') {
          if (this.state.custom.valid) {
            const { lengthMm, widthMm } = this.state.custom;
            summary = this.state.unit === 'm'
              ? `${this.trimDecimal((widthMm / 10).toFixed(1))} × ${this.trimDecimal((lengthMm / 10).toFixed(1))} cm (width × length) · ${this.copy.price_request}`
              : `${this.trimDecimal((Math.round(widthMm / 25.4) / 12).toFixed(3))}' × ${this.trimDecimal((Math.round(lengthMm / 25.4) / 12).toFixed(3))}' (width × length) · ${this.copy.price_request}`;
          } else summary = this.copy.price_request;
        } else if (this.state.selectionMode === 'preset' && this.selectedVariant) {
          const label = this.displaySizeLabel(this.selectedVariant.dataset.variantTitle);
          summary = `${label} · ${this.selectedVariant.dataset.price}`;
        }
        this.querySelectorAll('[data-mto-context-size]').forEach((output) => { output.textContent = summary; });
      }

      chooseCustomerType(type) {
        this.state.customerType = type;
        if (type === 'pro' && this.dataset.customerSignedIn !== 'true') {
          this.saveState({ reason: 'login', customerType: 'pro' });
          window.location.assign(this.dataset.accountUrl);
          return;
        }

        this.prepareRequestForm(type);
        this.showStep('request');
      }

      prepareRequestForm(type) {
        const isPro = type === 'pro';
        this.personalFields.hidden = isPro;
        this.proSummary.hidden = !isPro;
        this.personalInputs.forEach((input) => {
          input.disabled = isPro;
        });
        this.proEmailInput.disabled = !isPro;
        this.requestTitle.textContent = this.state.selectionMode === 'custom' ? 'Request this custom size' : 'Ask about this rug';
        this.requestIntro.textContent = isPro
          ? 'Review the signed-in account below, then send the request to Vorug.'
          : 'Share your contact details and a Vorug advisor will follow up about this request.';
        this.prepareRequestPayload(false);
      }

      prepareRequestPayload(markBusy = false) {
        const isCustom = this.state.selectionMode === 'custom';
        const selected = isCustom ? null : this.selectedVariant;
        const dimensions = isCustom ? this.formatCustomDimensions() : '';
        const requestType = isCustom ? 'Custom-size quote request' : 'Product enquiry';
        const customerType = this.state.customerType === 'pro' ? 'Pro Trade account' : 'Personal customer';
        const variant = selected ? `${selected.dataset.variantTitle} (variant ${selected.value})` : 'Custom size';
        const fullAmount = selected?.dataset.price || 'Price on request';
        const tradeStatus = this.dataset.proTradeVerified === 'true'
          ? 'Verified'
          : this.dataset.customerSignedIn === 'true' && this.state.customerType === 'pro'
            ? 'Verification required'
            : 'Not applicable';

        this.setRequestField('request-type', requestType);
        this.setRequestField('customer-type', customerType);
        this.setRequestField('product', `${this.dataset.productTitle} — ${window.location.origin}${this.dataset.productUrl}`);
        this.setRequestField('variant', variant);
        this.setRequestField('full-amount', fullAmount);
        this.setRequestField('dimensions', dimensions || 'Not applicable');
        this.setRequestField('trade-status', tradeStatus);
        this.setRequestField(
          'body',
          [
            requestType,
            `Customer route: ${customerType}`,
            `Product: ${this.dataset.productTitle}`,
            `Product URL: ${window.location.origin}${this.dataset.productUrl}`,
            `Selection: ${variant}`,
            `Full amount: ${fullAmount}`,
            `Custom dimensions: ${dimensions || 'Not applicable'}`,
            `Trade status: ${tradeStatus}`
          ].join('\n')
        );

        const submitButton = this.querySelector('[data-mto-request-submit]');
        if (submitButton && markBusy) {
          submitButton.setAttribute('aria-busy', 'true');
          window.setTimeout(() => submitButton.removeAttribute('aria-busy'), 1200);
        }
      }

      setRequestField(name, value) {
        const field = this.requestFields.get(name);
        if (field) field.value = value;
      }

      formatCustomDimensions() {
        const length = this.state.custom.lengthMm;
        const width = this.state.custom.widthMm;
        if (!Number.isFinite(length) || !Number.isFinite(width)) return '';
        const metric = `${this.trimDecimal((width / 10).toFixed(1))} X ${this.trimDecimal((length / 10).toFixed(1))} cm`;
        const imperial = `${this.mmToImperial(width)} X ${this.mmToImperial(length)}`;
        return `${metric} (width X length; ${imperial}; ${width} × ${length} mm)`;
      }

      mmToImperial(mm) {
        const totalInches = Math.round(mm / 25.4);
        return `${Math.floor(totalInches / 12)}'${totalInches % 12}\"`;
      }

      saveState(extra = {}) {
        const state = {
          version: 2,
          unit: this.state.unit,
          selectionMode: this.state.selectionMode,
          variantId: this.selectedVariant?.value || null,
          previousPresetVariantId: this.customFlowActive ? this.customReturnVariantId : null,
          payment: this.state.payment,
          customerType: extra.customerType || this.state.customerType,
          custom: {
            lengthMm: this.state.custom.lengthMm,
            widthMm: this.state.custom.widthMm
          },
          productUrl: this.dataset.productUrl,
          reason: extra.reason || 'navigation'
        };
        try {
          sessionStorage.setItem(this.storageKey, JSON.stringify(state));
        } catch (error) {
          // Navigation still works when session storage is unavailable.
        }
      }

      restoreState() {
        let rawState = null;
        try {
          rawState = sessionStorage.getItem(this.storageKey);
          if (rawState) sessionStorage.removeItem(this.storageKey);
        } catch (error) {
          return;
        }
        if (!rawState) return;

        try {
          const saved = JSON.parse(rawState);
          if (saved.version !== 2 || saved.productUrl !== this.dataset.productUrl) return;
          this.setUnit(saved.unit, { preserveCustom: false });

          if (saved.selectionMode === 'custom') {
            this.customFlowActive = true;
            this.customReturnVariantId = saved.previousPresetVariantId || saved.variantId || null;
            this.state.selectionMode = 'custom';
            this.state.custom.lengthMm = Number(saved.custom?.lengthMm) || null;
            this.state.custom.widthMm = Number(saved.custom?.widthMm) || null;
            this.populateCustomInputsFromState();
            this.validateCustom(false);
          } else if (saved.variantId) {
            const sizeInput = this.sizeInputs.find((input) => input.value === String(saved.variantId));
            if (sizeInput && !sizeInput.disabled) {
              sizeInput.checked = true;
              this.selectSize(sizeInput);
            }
          }

          if (saved.selectionMode === 'custom' && saved.reason === 'login' && saved.customerType === 'pro' && this.dataset.customerSignedIn === 'true') {
            this.state.customerType = 'pro';
            this.prepareRequestForm('pro');
            this.openDrawer({ step: 'request' });
            return;
          }

          if (saved.selectionMode === 'custom') this.openDrawer({ step: 'custom' });
          else this.focusInlineChooser();
        } catch (error) {
          console.warn('Could not restore the made-to-order purchase state.', error);
        }
      }

      async readCustomCart() {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store', signal: AbortSignal.timeout(10000) });
        if (!response.ok) throw new Error(this.copy.cart_error);
        return response.json();
      }

      customCartLine(cart, intent) {
        if (!this.customQuote || !intent || cart.currency !== this.customQuote.currency) return null;
        const expectedOptions = this.variantOptionValues.get(this.customQuote.variantId);
        if (!expectedOptions) return null;
        return cart.items?.find((item) => String(item.variant_id) === this.customQuote.variantId && item.sku === this.customQuote.sku
          && item.original_price === this.customQuote.priceCents && item.quantity >= 1
          && item.variant_options?.length === expectedOptions.length && expectedOptions.every((value, index) => item.variant_options[index] === value)
          && item.properties?._vorug_add_intent === intent.intent) || null;
      }

      guardCustomCheckout() {
        this.releaseCustomCheckoutGuard?.();
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return () => {};
        const disabled = new Map();
        const controls = '#CartDrawer-Checkout, [name="checkout"]';
        const lock = () => drawer.querySelectorAll(controls).forEach((button) => {
          if (!disabled.has(button)) disabled.set(button, button.disabled);
          button.disabled = true;
        });
        const prevent = (event) => {
          if (event.type === 'submit' || event.target.closest(controls)) { event.preventDefault(); event.stopImmediatePropagation(); }
        };
        drawer.addEventListener('click', prevent, true);
        drawer.addEventListener('submit', prevent, true);
        const observer = new MutationObserver(lock);
        observer.observe(drawer, { childList: true, subtree: true });
        lock();
        return () => {
          observer.disconnect();
          drawer.removeEventListener('click', prevent, true);
          drawer.removeEventListener('submit', prevent, true);
          disabled.forEach((original, button) => { if (button.isConnected) button.disabled = original; });
          this.releaseCustomCheckoutGuard = null;
        };
      }

      async removeMismatchedCustomLine(cart, intent) {
        const line = cart.items?.find((item) => String(item.variant_id) === this.customQuote?.variantId && item.properties?._vorug_add_intent === intent?.intent);
        if (!line?.key) throw new Error(this.copy.cart_error);
        const drawer = document.querySelector('cart-drawer');
        const sections = drawer?.getSectionsToRender().map((section) => section.id) || [];
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0, sections, sections_url: location.pathname }), signal: AbortSignal.timeout(10000)
        });
        if (!response.ok) throw new Error(this.copy.cart_error);
        const changed = await response.json();
        const verified = await this.readCustomCart();
        if (verified.items?.some((item) => item.properties?._vorug_add_intent === intent.intent)) throw new Error(this.copy.cart_error);
        if (changed.sections && drawer) drawer.renderContents(changed);
      }

      clearCustomAddIntent() {
        this.releaseCustomCheckoutGuard?.();
        this.customAddIntent = null;
        try { sessionStorage.removeItem(`mto-custom-add-${this.dataset.productId}`); } catch (error) {}
      }

      async prepareCustomAddIntent() {
        const key = `mto-custom-add-${this.dataset.productId}`;
        const saved = sessionStorage.getItem(key);
        if (saved) {
          const intent = JSON.parse(saved);
          if (intent.quoteId !== this.customQuote.quoteId) {
            this.setCartStatus(this.copy.custom_cart_pending || this.copy.cart_error);
            return false;
          }
          const cart = await this.readCustomCart();
          if (!this.customCartLine(cart, intent)) {
            this.setCartStatus(this.copy.custom_cart_pending || this.copy.cart_error);
            return false;
          }
          this.clearCustomAddIntent();
          this.setCartStatus(this.copy.cart_success);
          document.querySelector('cart-drawer')?.open();
          return false;
        }
        this.customAddIntent = { intent: crypto.randomUUID(), quoteId: this.customQuote.quoteId };
        sessionStorage.setItem(key, JSON.stringify(this.customAddIntent));
        return true;
      }

      async addFullPaymentToCart() {
        if (!this.canPurchase() || !this.nativeForm || this.cartPending) {
          this.setCartStatus(this.selectedVariant?.disabled ? this.copy.unavailable_status : this.copy.select_status);
          return;
        }
        const idInput = this.nativeForm.querySelector('input[name="id"]');
        const submitButton = this.nativeForm.querySelector('[type="submit"]');
        if (!idInput || !submitButton) { this.setCartStatus(this.copy.cart_error); return; }
        if (this.customQuote) {
          this.customAddChecking = true;
          this.syncInline();
          try { if (!await this.prepareCustomAddIntent()) return; }
          catch (error) { this.setCartStatus(this.copy.custom_cart_pending || this.copy.cart_error); return; }
          finally { this.customAddChecking = false; this.syncInline(); }
        }
        this.syncProductionTimeProperty();
        this.syncEnteredDimensionsProperties();
        idInput.value = this.selectedVariant.value;
        idInput.disabled = false;
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-disabled');
        this.cartPending = true;
        this.addButton.disabled = true;
        this.addButton.setAttribute('aria-busy', 'true');
        this.addButton.textContent = this.copy.adding;
        this.syncInline();
        this.actionInstances.forEach((actions) => {
          const button = actions.querySelector('[data-mto-inline-add]');
          button.setAttribute('aria-busy', 'true');
          button.querySelector('span').textContent = this.copy.adding;
        });
        this.setCartStatus(this.copy.adding_status);
        this.track('pdp_add_to_cart_attempt', { variant_id: this.selectedVariant.value, variant_title: this.selectedVariant.dataset.variantTitle, price: this.selectedVariant.dataset.price });
        if (this.customQuote) this.releaseCustomCheckoutGuard = this.guardCustomCheckout();
        this.nativeForm.requestSubmit(submitButton);
      }

      handleKeydown(event) {
        if (!this.isOpen) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeDrawer();
          return;
        }

        if (event.key !== 'Tab') return;
        const focusable = Array.from(
          this.drawer.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((element) => !element.hidden && element.offsetParent !== null);

        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
}

